import { Action } from '@reduxjs/toolkit';
import React, { FC, PropsWithChildren, useState } from 'react';
import { EventsType, IdleTimerProvider } from 'react-idle-timer';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';

import { get } from '@eika-infrastruktur/request';

import { useBankregnr, useData } from '../../config';
import { getRequestOptions, updateValidSession } from '../../meldskade/reducer/skademelding-actions';
import { DeveloperFlytHelper } from '../../test/developer-flyt-helper';
import { isNonProductionEnvironment } from '../../test/developer-page';
import { Skademeldingstype } from '../../types/generated-types';
import { hentKeepAliveUrl } from '../../urler';
import InactiveModal from '../inactive-modal/inactive-modal';

export const maxSessionUpdateFrequencyInSeconds = 120;
export const timeBeforeInactiveModalToShowInSeconds = 10 * 60;

interface IdleTimerProviderMeldSkadeProps {
    skademeldingstype: Skademeldingstype | string;
}

export const IdleTimerProviderMeldSkade: FC<PropsWithChildren<IdleTimerProviderMeldSkadeProps>> = ({
    children,
    skademeldingstype,
}) => {
    const dispatch: Dispatch<Action> = useDispatch();
    const bankregnr = useBankregnr();
    const isDeveloperpageenabled = useData('developerpageenabled');

    const [openInactiveModal, setOpenInactiveModal] = useState<boolean>(false);
    const [sessionUpdatedAt, setSessionUpdatedAt] = useState<Date>(new Date(Date.now()));

    const updateSessionRequest = (bankregnr: string) => {
        get(hentKeepAliveUrl(), getRequestOptions(bankregnr))
            .then()
            .catch((e) => {
                if (e.status.valueOf() === 401) {
                    dispatch(updateValidSession());
                }
            });
    };

    const onPrompt = () => {
        setOpenInactiveModal(true);
    };

    const onAction = () => {
        const secondsSinceLastSessionUpdated = (Date.now() - sessionUpdatedAt.getTime()) / 1000;
        if (secondsSinceLastSessionUpdated > maxSessionUpdateFrequencyInSeconds) {
            updateSessionRequest(bankregnr);
            setSessionUpdatedAt(new Date(Date.now()));
        }
    };

    const closeModal = () => {
        updateSessionRequest(bankregnr);
        setOpenInactiveModal(false);
    };

    const eventsTriggeringSessionRefresh: EventsType[] = ['keypress'];

    return (
        <IdleTimerProvider
            onAction={onAction}
            events={eventsTriggeringSessionRefresh}
            onPrompt={onPrompt}
            promptBeforeIdle={500}
            timeout={timeBeforeInactiveModalToShowInSeconds * 1000}
            stopOnIdle={false}
        >
            {children}
            {isNonProductionEnvironment() && isDeveloperpageenabled && (
                <DeveloperFlytHelper
                    sessionUpdatedAt={sessionUpdatedAt}
                    maxSessionUpdateFrequencyInSeconds={maxSessionUpdateFrequencyInSeconds}
                />
            )}
            <InactiveModal
                skademeldingstype={skademeldingstype}
                openModal={openInactiveModal}
                setOpen={() => setOpenInactiveModal(true)}
                closeModal={closeModal}
                setSessionUpdatedToNow={() => setSessionUpdatedAt(new Date(Date.now()))}
            />
        </IdleTimerProvider>
    );
};
